import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Users,
  ExternalLink,
  MessageSquare,
  CreditCard,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Business } from '@/types/homepage';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { optimizedImage } from '@/lib/cloudinaryImage';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-label="WhatsApp"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

interface MobileBusinessListItemProps {
  business: Business;
  activities?: any[];
  onSelect?: (business: Business | null) => void;
  onCustomAdventure?: (businessId: string) => void;
  hasCustomAdventures?: boolean;
  className?: string;
}

export function MobileBusinessListItem({
  business,
  activities = [],
  onSelect,
  onCustomAdventure,
  hasCustomAdventures = false,
  className
}: MobileBusinessListItemProps) {
  const navigate = useNavigate();
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  // Get booking info
  const bookingInfo = {
    mode: business.contact?.bookingMode || 'platform',
    url: business.contact?.bookingUrl,
    email: business.contact?.email,
    whatsapp: business.contact?.whatsappNumber
  };

  // Get experience type info
  const getExperienceTypeInfo = () => {
    const hasAnimal = business.hasAnimalActivities;
    const hasAdventure = business.hasAdventureActivities;
    
    if (hasAnimal && hasAdventure) {
      return { label: 'Animal & Adventure', icon: '🦁🏔️', color: 'bg-emerald-100 text-emerald-800' };
    } else if (hasAnimal) {
      return { label: 'Animal Experience', icon: '🦁', color: 'bg-orange-100 text-orange-800' };
    } else if (hasAdventure) {
      return { label: 'Adventure', icon: '🏔️', color: 'bg-blue-100 text-blue-800' };
    }
    return { label: 'Experience', icon: '🌟', color: 'bg-gray-100 text-gray-800' };
  };

  const experienceType = getExperienceTypeInfo();

  // Handle booking action
  const handleBooking = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isBookingLoading) return;
    
    if (bookingInfo.mode === 'redirect' && bookingInfo.url) {
      window.open(bookingInfo.url, '_blank');
      toast.success('Opening external booking page...');
    } else if (bookingInfo.mode === 'platform') {
      setIsBookingLoading(true);
      try {
        navigate(`/business/${business._id}#activities`);
      } catch (error) {
        toast.error('Failed to load business page');
      } finally {
        setIsBookingLoading(false);
      }
    } else {
      onSelect?.(business);
    }
  }, [bookingInfo, activities, navigate, onSelect, isBookingLoading]);

  // Handle contact
  const handleContact = useCallback((method: 'whatsapp' | 'email', e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (method === 'whatsapp' && bookingInfo.whatsapp) {
      const cleanNumber = bookingInfo.whatsapp.replace(/\D/g, '');
      window.open(`https://wa.me/${cleanNumber}`, '_blank');
      toast.success('Opening WhatsApp...');
    } else if (method === 'email' && bookingInfo.email) {
      window.open(`mailto:${bookingInfo.email}`, '_blank');
      toast.success('Opening email client...');
    }
  }, [bookingInfo]);

  // Handle custom adventure click
  const handleCustomAdventureClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onCustomAdventure?.(business._id);
  }, [business._id, onCustomAdventure]);

  // Handle business details click
  const handleBusinessClick = useCallback(() => {
    navigate(`/business/${business._id}`);
  }, [business._id, navigate]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "bg-white rounded-xl border border-gray-200 shadow-sm",
        "hover:shadow-md transition-all duration-300",
        "touch-manipulation overflow-hidden",
        "min-h-[160px] sm:min-h-[180px]", // Increased min-height for better content fit
        className
      )}
      onClick={() => onSelect?.(business)}
    >
      <div className="flex h-full">
        {/* Image Section - Left Side */}
        <div className="w-[130px] sm:w-[150px] flex-shrink-0 relative">
          <div className="w-full h-full relative overflow-hidden rounded-l-xl">
            {business.images?.cover || business.images?.logo || business.images?.gallery?.[0] ? (
              <img
                src={optimizedImage(
                  Array.isArray(business.images?.cover) 
                    ? business.images.cover[0] 
                    : business.images?.cover || business.images?.logo || business.images?.gallery?.[0],
                  800
                )}
                alt={business.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src="/images/default-business.jpg"
                alt={business.name || 'Business'}
                className="w-full h-full object-cover opacity-90"
              />
            )}
            
            {/* Emoji overlay */}
            <div className="absolute top-3 left-3">
              <div className="w-8 h-8 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md">
                <span className="text-lg">{business.emoji || '🏞️'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section - Right Side */}
        <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
          {/* Header */}
          <div className="space-y-2">
            {/* Business name - Full width for better readability */}
            <div className="space-y-1">
              <h3 
                className="font-semibold text-gray-900 line-clamp-1 text-base cursor-pointer hover:text-emerald-600 transition-colors leading-tight"
                onClick={handleBusinessClick}
              >
                {business.name}
              </h3>
              {/* Experience type badge - Below title to prevent collision */}
              <div className="flex items-center">
                <Badge 
                  variant="secondary" 
                  className={cn(
                    "text-xs px-2 py-0.5 font-medium",
                    experienceType.color
                  )}
                >
                  <span className="mr-1">{experienceType.icon}</span>
                  {experienceType.label}
                </Badge>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center text-gray-500 text-sm">
              <MapPin className="h-4 w-4 mr-1.5 shrink-0 text-emerald-600" />
              <span className="truncate">
                {business.location?.address?.city || 'Location'}, {business.location?.address?.country || 'Country'}
              </span>
            </div>

            {/* Description */}
            <p className="text-gray-600 line-clamp-2 text-sm leading-relaxed">
              {business.description || 'Experience the wildlife adventure of a lifetime with our expert guides.'}
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Activities info */}
            {activities.length > 0 && (
              <div className="flex items-center text-sm text-gray-500">
                <Users className="h-4 w-4 mr-1.5" />
                <span className="font-medium">{activities.length} {activities.length === 1 ? 'activity' : 'activities'}</span>
              </div>
            )}

            {/* Action buttons - Optimized for mobile popup */}
            <div className="flex gap-1.5">
              {/* Main booking button - Larger and more prominent */}
              <Button
                onClick={handleBooking}
                disabled={isBookingLoading}
                size="sm"
                className={cn(
                  "flex-1 min-h-[44px] text-sm font-semibold",
                  "bg-emerald-600 hover:bg-emerald-700 text-white",
                  "touch-manipulation rounded-lg"
                )}
              >
                {isBookingLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                ) : (
                  <>
                    {bookingInfo.mode === 'redirect' ? (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                  </>
                )}
                <span>
                  {isBookingLoading ? 'Loading...' : bookingInfo.mode === 'redirect' ? 'Visit' : 'Book Now'}
                </span>
              </Button>

              {/* Contact buttons - Compact but touch-friendly */}
              <div className="flex gap-1">
                {bookingInfo.whatsapp && (
                  <Button
                    onClick={(e) => handleContact('whatsapp', e)}
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] min-w-[44px] h-11 w-11 p-0 border-green-500 text-green-600 hover:bg-green-50 touch-manipulation rounded-lg flex items-center justify-center"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                  </Button>
                )}
                
                {bookingInfo.email && (
                  <Button
                    onClick={(e) => handleContact('email', e)}
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] min-w-[44px] h-11 w-11 p-0 border-blue-500 text-blue-600 hover:bg-blue-50 touch-manipulation rounded-lg flex items-center justify-center"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Custom adventure button - Better sizing and prominence */}
            {hasCustomAdventures && (
              <Button
                onClick={handleCustomAdventureClick}
                variant="outline"
                size="sm"
                className={cn(
                  "w-full min-h-[44px] text-sm font-medium",
                  "border-emerald-500 text-emerald-600 hover:bg-emerald-50",
                  "touch-manipulation rounded-lg"
                )}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                <span>Custom Adventure</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
} 